import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { FilterPanelContainer, NoDataBox, PageContainer } from 'components/GlobalStyles/styles';
import SnackBar, { MessageType } from 'components/SnackBar';
import PeriodSelector from 'features/PeriodSelector';
import { StartGridItem } from 'pages/TeamOverviewDetail/styles';
import { dateFilterOptions } from 'pages/Teams/Teams';
import { useResourceUtilizationQuery } from 'services/busmanApi';
import { JOB_TYPE_LABELS } from 'utils/constants';
import { getDatesBetween } from 'utils/date';

import { RootState } from '../../store';

import { ItemListContainer, OverviewDetailBox } from './styles';
import { JobDetailWithWorklogs } from './types';

const jobTypeMap: Record<string, string> = {
  Quoted: 'Quoted',
  'Time Billed': 'TimeBilled',
  Internal: 'Internal',
};
/**
 * Display detailed resource utilization for a specific employee within a team,
 * including a breakdown of hours worked on different jobs over a selected date range.
 *
 * NAVIGATION VIEW: /teams/overview/:teamID/:employeeID
 *
 */
const TeamOverviewEmpDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const teamId = location.pathname.split('/')[3];
  const employeeId = location.pathname.split('/')[4];
  const [message, setMessage] = useState<MessageType | null>(null);
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Refs for synchronized scrolling
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const worklogScrollRef = useRef<(HTMLDivElement | null)[]>([]);

  const [jobTypeFilter, setJobTypeFilter] = useState<string>('All');
  const { fromDateState, toDateState, selectedFilterState } = useSelector((state: RootState) => state.periodSelector);
  const [dateFilters, setDateFilters] = useState(selectedFilterState || dateFilterOptions[0]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    fromDateState ? dayjs(fromDateState) : dayjs().subtract(1, 'day'),
  );
  const [toDate, setToDate] = useState<Dayjs | null>(toDateState ? dayjs(toDateState) : dayjs());
  const formattedStartDate = fromDate ? fromDate.format('YYYY-MM-DD') : '';
  const formattedEndDate = toDate ? toDate.format('YYYY-MM-DD') : '';
  const navigateToDetails = () => {
    navigate(`/teams/overview/${teamId}`);
  };

  const {
    data: empResourceData,
    isFetching: isResourceDataFetching,
    error,
  } = useResourceUtilizationQuery(
    {
      team_id: teamId || '',
      employee_id: employeeId || '',
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    },
    {
      skip: !formattedStartDate || !formattedEndDate,
    },
  );

  React.useEffect(() => {
    if (error && 'data' in error) {
      const errData = error.data as { errors?: string } | undefined;
      setMessage({ type: 'error', msg: `${errData?.errors ?? 'An error occurred'}` });
    }
  }, [error]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resources = empResourceData?.resources ?? [];
  const teamName = resources.length > 0 ? empResourceData.teams?.team_name : 'Team';

  const employeeName = resources[0]?.firstname || 'Employee';
  const workedJobs =
    jobTypeFilter === 'All'
      ? resources[0]?.jobs
      : resources[0]?.jobs.filter((job: JobDetailWithWorklogs) => job.job_type === jobTypeMap[jobTypeFilter]);

  const dateRange = getDatesBetween(dayjs(formattedStartDate), dayjs(formattedEndDate));

  const tableDataTemp: {
    job_number: string;
    worklog: {
      [worklog_date: string]: {
        hours: number;
        isOverBudget: boolean;
      };
    };
  }[] = [];

  let displayPeriods: string[] = [];
  workedJobs?.forEach((job: JobDetailWithWorklogs) => {
    const worklogObj: { [worklog_date: string]: { hours: number; isOverBudget: boolean } } = {};

    if (dateRange.length > 8) {
      const weeks = dateRange.reduce<string[][]>((acc, date, i) => {
        const weekIndex = Math.floor(i / 7);
        if (!acc[weekIndex]) acc[weekIndex] = [];
        acc[weekIndex].push(date);
        return acc;
      }, []);

      displayPeriods = weeks.map((week) => {
        const startDate = dayjs(week[0]).format('MMM DD');
        const endDate = dayjs(week[week.length - 1]).format('MMM DD');
        return `${startDate} - ${endDate}`;
      });

      weeks.forEach((week, weekIndex) => {
        const weekIdentifier = displayPeriods[weekIndex];
        let weeklyHours = 0;

        week.forEach((date) => {
          const dayWorklog = job.worklogs_meta?.find((w) => w.worklog_date === date);
          if (dayWorklog) {
            weeklyHours += dayWorklog.worklog_hours;
          }
        });

        worklogObj[weekIdentifier] = { hours: weeklyHours, isOverBudget: job.is_overrun };
      });
    } else {
      job.worklogs_meta?.forEach((worklog) => {
        worklogObj[worklog.worklog_date] = {
          hours: worklog.worklog_hours,
          isOverBudget: job.is_overrun,
        };
      });
    }

    tableDataTemp.push({
      job_number: job.job_number,
      worklog: worklogObj,
    });
  });

  // Synchronize horizontal scrolling between header and worklog rows
  useEffect(() => {
    const headerEl = headerScrollRef.current;
    const worklogsEls = worklogScrollRef.current.filter((el) => el !== null) as HTMLDivElement[];

    if (!headerEl || worklogsEls.length === 0) return;

    const allScrollableElements = [headerEl, ...worklogsEls];

    const syncScroll = (source: HTMLDivElement) => {
      return () => {
        allScrollableElements.forEach((target) => {
          if (target !== source) {
            target.scrollLeft = source.scrollLeft;
          }
        });
      };
    };

    const handlers = allScrollableElements.map((el) => {
      const handler = syncScroll(el);
      el.addEventListener('scroll', handler);
      return { element: el, handler };
    });

    return () => {
      handlers.forEach(({ element, handler }) => {
        element.removeEventListener('scroll', handler);
      });
    };
  }, [dateRange.length, tableDataTemp.length]);

  const handleJobTypeFilter = (event: SelectChangeEvent) => {
    const selectedFilterValue = event.target.value as string;
    setJobTypeFilter(selectedFilterValue);
  };

  const aside = () => {
    return (
      <>
        <FilterPanelContainer>
          {!isMobileScreen && (
            <Box
              sx={{
                mb: 2,
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                gap: theme.spacing(1),
                alignItems: 'center',
              }}>
              <Box onClick={navigateToDetails} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <ArrowBackIosIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ paddingTop: '4px' }}>
                {teamName}
              </Typography>
            </Box>
          )}

          <Box sx={{ paddingY: theme.spacing(1) }}>
            <Typography variant="h6" gutterBottom>
              Job Types
            </Typography>
            <Select
              size="small"
              value={jobTypeFilter}
              onChange={handleJobTypeFilter}
              sx={{
                width: '100%',
              }}>
              {[...JOB_TYPE_LABELS, 'All'].map((filter) => {
                return (
                  <MenuItem key={filter} value={filter}>
                    {filter}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>

          <PeriodSelector
            selectedFilter={dateFilters}
            fromDate={fromDate}
            toDate={toDate}
            onFilterChange={setDateFilters}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </FilterPanelContainer>
      </>
    );
  };

  return (
    <>
      <PageContainer>
        {aside()}
        {isResourceDataFetching ? (
          <NoDataBox>
            <Typography sx={{ padding: theme.spacing(2) }} variant="h6">
              Loading team data...
            </Typography>
          </NoDataBox>
        ) : resources.length === 0 || error ? (
          <NoDataBox>
            <Typography sx={{ padding: theme.spacing(2) }} variant="h6">
              No resource utilization data found for the selected period.
            </Typography>
          </NoDataBox>
        ) : (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: theme.spacing(1),
              flexDirection: { xs: 'column', md: 'row' },
            }}>
            <OverviewDetailBox>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ color: theme.palette.text.primary, padding: theme.spacing(2) }}>
                {employeeName}&apos;s Worklog Overview
              </Typography>
              <Divider />

              <ItemListContainer data-testid="scrollable-table">
                <Grid container display="flex" flexDirection="row" sx={{ position: 'relative' }}>
                  <StartGridItem item md={2}>
                    <Typography variant="body2" gutterBottom sx={{ color: '#9E9E9E' }}>
                      JOB NO.
                    </Typography>
                  </StartGridItem>

                  <Box
                    ref={headerScrollRef}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      overflowX: 'auto',
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}>
                    {(displayPeriods.length > 0 ? displayPeriods : dateRange).map((period, index) => (
                      <Grid
                        item
                        key={index}
                        md={2}
                        sx={{
                          minWidth: 100,
                          display: 'flex',
                          padding: '2px',
                          color: '#9E9E9E',
                          justifyContent: 'center',
                        }}>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: displayPeriods.length > 0 ? '0.75rem' : 'inherit' }}>
                          {displayPeriods.length > 0 ? period : dayjs(period).format('MMM DD')}
                        </Typography>
                      </Grid>
                    ))}
                  </Box>
                </Grid>

                {/* Worklog table */}
                {tableDataTemp.map((jobData, index) => (
                  <Grid
                    container
                    key={index}
                    display="flex"
                    flexDirection="row"
                    sx={{
                      position: 'relative',
                      minHeight: '48px',
                      cursor: 'pointer',
                      alignItems: 'center',
                      display: 'flex',
                      border: '1px solid',
                      borderRadius: '2px',
                      borderColor: theme.palette.divider,
                      backgroundColor: index % 2 === 0 ? theme.palette.action.hover : 'transparent',
                      '&:hover': { backgroundColor: theme.palette.action.selected },
                    }}>
                    <StartGridItem item md={2}>
                      <Typography variant="body1">{jobData.job_number}</Typography>
                    </StartGridItem>

                    <Box
                      ref={(el: HTMLDivElement | null) => (worklogScrollRef.current[index] = el)}
                      data-testid={`table-data-item-${index}`}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                      }}>
                      {(displayPeriods.length > 0 ? displayPeriods : dateRange).map((period, idx) => (
                        <Grid item key={idx} md={2} sx={{ minWidth: 100, display: 'flex', justifyContent: 'center' }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: jobData.worklog[period]?.isOverBudget ? theme.palette.error.main : 'inherit',
                              fontWeight: jobData.worklog[period]?.isOverBudget ? 'bold' : 'normal',
                            }}>
                            {jobData.worklog[period]?.hours ? jobData.worklog[period].hours : '-'}
                          </Typography>
                        </Grid>
                      ))}
                    </Box>
                  </Grid>
                ))}
              </ItemListContainer>
            </OverviewDetailBox>
          </Box>
        )}
      </PageContainer>
      <SnackBar message={message} onClose={() => setMessage(null)} />
    </>
  );
};

export default TeamOverviewEmpDetail;
